/* Copyright (c) 2025 Xperi. All Rights Reserved. */

import { ScreenType, VodLaunchDetails } from 'client-code/vod-browse';

import { ErrorTypes } from '../../core/error';
import { JumpChannelError } from '../../core/error/JumpChannelError';
import { JumpChannelErrorMapper } from '../../core/errormapper';
import { AxiosErrorAdapter } from '../../core/errormapper/adapters';
import { VodInteractor } from '../../core/interactors';
import { Locale, LocaleKeys } from '../../core/locale';
import { Log } from '../../core/logging';
import { ScreenTaskControllerTypes } from '../../core/navigation';
import { TListViewType } from '../../core/types';
import { OverlayTypes } from '../overlay/OverlayTypes';
import { showOverlayDialog } from '../overlaydialog/overlaydialog.reducers';
import { IOverlayDialog } from '../overlaydialog/overlaydialogprops';
import { galleryScreenTransition } from '../screens/navigation/screensnavigation';
import {
    setScreenTaskController,
    showListViewScreen,
    showVodBrowseMainScreen,
} from '../screens/screens.reducers';

const TAG: string = 'jumpchannel.reducers';

export const showJumpChannelErrorOverlay =
    (error: JumpChannelError, overlayType?: OverlayTypes) => (dispatch: Function) => {
        const overlayOptions = [{ text: Locale.getString(LocaleKeys.OVERLAY_OPTION_OK_TEXT) }];
        const overlayDialogProps: IOverlayDialog = {
            description: error.getDescription(),
            hasDimmer: true,
            hasSeperator: false,
            messageCode: error.getCode(),
            options: overlayOptions,
            title: error.getTitle(),
            overlayType: overlayType ?? OverlayTypes.ERROR,
        };

        dispatch(
            setScreenTaskController(ScreenTaskControllerTypes.JUMP_CHANNEL_ERROR_OVERLAY_TASKS),
        );
        dispatch(showOverlayDialog(overlayDialogProps));
    };

export const launchVodScreen =
    (
        vodJumpChannelNumber: string,
        callback?: { onSuccess?: Function; onError?: Function },
        overlayType?: OverlayTypes,
    ) =>
    async (dispatch: Function) => {
        try {
            const vodServices = new VodInteractor();
            const vodLaunchInfo: VodLaunchDetails =
                await vodServices.getVodLaunchInfo(vodJumpChannelNumber);

            const { isAdult, isRoot, partnerId, screenType, title, vodItemId } = vodLaunchInfo;
            const isGalleryScreen =
                screenType?.toLowerCase() === ScreenType.PosterBrowseMixed.toLowerCase() ||
                screenType?.toLowerCase() === ScreenType.PosterBrowseMovie.toLowerCase();
            const isRentalScreen =
                screenType?.toLowerCase() === ScreenType.TextCurrentRentals.toLowerCase();

            if (isRoot || screenType?.toLowerCase() === ScreenType.LeftNavigation.toLowerCase()) {
                dispatch(showVodBrowseMainScreen());
                Log.i(TAG, 'Launching VOD Browse Main Screen as root.');
            } else if (isGalleryScreen && vodItemId) {
                dispatch(
                    galleryScreenTransition(
                        !!isAdult,
                        vodItemId,
                        partnerId ?? '',
                        screenType,
                        title ?? '',
                    ),
                );
                Log.i(TAG, 'Launching VOD Gallery Screen with parentVodItemId: ', vodItemId);
            } else if (isRentalScreen && vodItemId) {
                dispatch(
                    showListViewScreen({
                        id: vodItemId,
                        title: title ?? '',
                        listType: ScreenType.TextCurrentRentals as TListViewType,
                    }),
                );
                Log.i(TAG, 'Launching VOD List View Screen with vodItemId: ', vodItemId);
            } else {
                Log.i(TAG, 'VOD Launch info is not available', vodLaunchInfo);
            }
            if (callback?.onSuccess) {
                callback.onSuccess();
                Log.i(TAG, 'Callback function found. Executing it.');
            }
        } catch (error) {
            Log.e(TAG, `Error launching VOD screen: ${error}`);

            if (callback?.onError) {
                callback.onError();
                Log.i(TAG, 'Callback function found. Executing it.');
            }

            // Show jump channel error overlay
            const jumpChannelError = new JumpChannelErrorMapper(
                new AxiosErrorAdapter(),
            ).getErrorByCode(ErrorTypes.JUMP_CHANNEL_UNAVAILABLE_V347, error);
            dispatch(showJumpChannelErrorOverlay(jumpChannelError, overlayType));
        }
    };
